import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";

import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PromotionDetails from "./PromotionDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import NumberRangeFilter from "designV2/components/NumberRangeFilter";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import TableToolbar from "designV2/components/TableToolbar";
import Button from "designV2/components/Button";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import { endPoint } from "utils/config";
import { readableError } from "../detailFields";
import "../superRide.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const PROMOTION_FILTERS = `
  condition: $condition
  type: $type
  promotionCode: $promotionCode
  percentFrom: $percentFrom
  percent: $percent
  maximumPromotionFrom: $maximumPromotionFrom
  maximumPromotion: $maximumPromotion
  fromFrom: $fromFrom
  from: $from
  toFrom: $toFrom
  to: $to
`;

const PROMOTION_VARIABLES = `
  $limit: Int
  $skip: Int
  $condition: promotionConditionEnum
  $type: promotionTypeEnum
  $promotionCode: String
  $percentFrom: Float
  $percent: Float
  $maximumPromotionFrom: Float
  $maximumPromotion: Float
  $fromFrom: Date
  $from: Date
  $toFrom: Date
  $to: Date
  $createdAtSort: Int
  $percentSort: Float
  $maximumPromotionSort: Float
`;

const GET_PROMOTIONS = gql`
  query getTripPromotionsByAdmin(${PROMOTION_VARIABLES}) {
    getTripPromotionsByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${PROMOTION_FILTERS} }
      sort: {
        createdAt: $createdAtSort
        percent: $percentSort
        maximumPromotion: $maximumPromotionSort
      }
    ) {
      _id
      promotionCode
      condition
      type
      for
      percent
      maximumPromotion
      useLimitCount
      canUse
      canNotUse
      from
      to
    }
    getTripPromotionsByAdminCount(filters: { ${PROMOTION_FILTERS} })
  }
`;

const DELETE_PROMOTIONS = gql`
  mutation deleteTripPromotionByAdmin($idSet: [ID!]!) {
    deleteTripPromotionByAdmin(idSet: $idSet) {
      _id
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const TYPE_OPTIONS = [
  { value: "PERCENT", label: "Percentage" },
  { value: "FIXED", label: "Fixed amount" },
];
const TYPE_LABELS = TYPE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const CONDITION_OPTIONS = [
  { value: "TIMELY", label: "Time limited" },
  { value: "FIRST_TRIP", label: "First trip only" },
];
const CONDITION_LABELS = CONDITION_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const FOR_LABELS = { RIDE: "Rides", DELIVERY: "Deliveries", ALL: "Rides and deliveries" };

// Live / scheduled / expired is derived from the window, not stored, so
// it is a page-local filter and says so when it narrows anything.
const LIFECYCLE_OPTIONS = [
  { value: "", label: "Any status" },
  { value: "live", label: "Live now" },
  { value: "scheduled", label: "Scheduled" },
  { value: "expired", label: "Expired" },
];
const LIFECYCLE_LABELS = LIFECYCLE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "valueHigh", label: "Value high-low" },
  { value: "valueLow", label: "Value low-high" },
];

const FILTER_DEBOUNCE_MS = 350;

const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "-");

const lifecycleOf = (promotion) => {
  const now = moment();
  if (promotion.to && moment(promotion.to).isBefore(now)) {
    return { key: "expired", tone: "neutral", label: "Expired" };
  }
  if (promotion.from && moment(promotion.from).isAfter(now)) {
    return { key: "scheduled", tone: "info", label: "Scheduled" };
  }
  return { key: "live", tone: "success", label: "Live" };
};

const valueOf = (promotion) =>
  promotion.type === "PERCENT"
    ? `${promotion.percent || 0}%`
    : `$${Number(promotion.percent || 0).toFixed(2)}`;

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const Promotions = () => {
  const [codeFilter, setCodeFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [conditionFilter, setConditionFilter] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState("");
  const [valueFrom, setValueFrom] = useState("");
  const [valueTo, setValueTo] = useState("");
  const [startFrom, setStartFrom] = useState("");
  const [startTo, setStartTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [promotionDetail, setPromotionDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState("");
  const [copiedCode, setCopiedCode] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({ limit: perPage, skip: 0, createdAtSort: -1 });

  useEffect(() => {
    const handler = setTimeout(() => {
      setVariables((v) => ({
        ...v,
        skip: 0,
        promotionCode: codeFilter.trim() || undefined,
        type: typeFilter || undefined,
        condition: conditionFilter || undefined,
        percentFrom: valueFrom ? parseFloat(valueFrom) : undefined,
        percent: valueTo ? parseFloat(valueTo) : undefined,
        fromFrom: startFrom || undefined,
        from: startTo || undefined,
        createdAtSort:
          sortOption === "oldest" ? 1 : sortOption === "newest" ? -1 : undefined,
        percentSort:
          sortOption === "valueHigh" ? -1 : sortOption === "valueLow" ? 1 : undefined,
      }));
      setPageCount(0);
      setSelectedIds([]);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [codeFilter, typeFilter, conditionFilter, valueFrom, valueTo, startFrom, startTo, sortOption]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
    setSelectedIds([]);
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_PROMOTIONS, {
    variables,
    fetchPolicy: "network-only",
  });
  const [fetchAllForExport, { data: exportData }] = useLazyQuery(GET_PROMOTIONS, {
    fetchPolicy: "network-only",
  });
  const [deletePromotions] = useMutation(DELETE_PROMOTIONS);

  const allPromotions = (data && data.getTripPromotionsByAdmin) || [];
  const serverCount = (data && data.getTripPromotionsByAdminCount) || 0;

  const promotions = lifecycleFilter
    ? allPromotions.filter((p) => lifecycleOf(p).key === lifecycleFilter)
    : allPromotions;

  const livePage = allPromotions.filter((p) => lifecycleOf(p).key === "live").length;
  const scheduledPage = allPromotions.filter((p) => lifecycleOf(p).key === "scheduled").length;
  const expiredPage = allPromotions.filter((p) => lifecycleOf(p).key === "expired").length;

  /* ---------------- selection + delete ---------------- */

  const toggleSelectOne = (id) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
    );

  const toggleSelectAll = () => {
    const ids = promotions.map((p) => p._id);
    setSelectedIds((current) => (current.length === ids.length ? [] : ids));
  };

  const checkboxState =
    promotions.length > 0 && selectedIds.length === promotions.length
      ? "checked"
      : selectedIds.length > 0
      ? "indeterminate"
      : "unchecked";

  const runDelete = () => {
    const targets = deleteTarget || [];
    setDeleting(true);
    setActionError("");
    deletePromotions({ variables: { idSet: targets.map((p) => p._id) } })
      .then(() => {
        setDeleting(false);
        setDeleteTarget(null);
        setSelectedIds([]);
        refetch();
      })
      .catch((err) => {
        setDeleting(false);
        setDeleteTarget(null);
        setActionError(readableError(err));
      });
  };

  const handleCopy = (code) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      window.setTimeout(() => setCopiedCode(""), 1500);
    });
  };

  /* ---------------- filter chips ---------------- */

  const activeFilterChips = [];
  if (codeFilter.trim()) activeFilterChips.push({ id: "code", label: `Code: ${codeFilter}` });
  if (typeFilter) activeFilterChips.push({ id: "type", label: `Type: ${TYPE_LABELS[typeFilter]}` });
  if (conditionFilter) {
    activeFilterChips.push({ id: "condition", label: CONDITION_LABELS[conditionFilter] });
  }
  if (lifecycleFilter) {
    activeFilterChips.push({ id: "lifecycle", label: LIFECYCLE_LABELS[lifecycleFilter] });
  }
  if (valueFrom || valueTo) {
    activeFilterChips.push({ id: "value", label: `Value: ${valueFrom || "0"} to ${valueTo || "-"}` });
  }
  if (startFrom || startTo) {
    activeFilterChips.push({ id: "start", label: `Starts: ${startFrom || "-"} to ${startTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "code") setCodeFilter("");
    if (id === "type") setTypeFilter("");
    if (id === "condition") setConditionFilter("");
    if (id === "lifecycle") setLifecycleFilter("");
    if (id === "value") {
      setValueFrom("");
      setValueTo("");
    }
    if (id === "start") {
      setStartFrom("");
      setStartTo("");
    }
  };

  const clearAllFilters = () => {
    setCodeFilter("");
    setTypeFilter("");
    setConditionFilter("");
    setLifecycleFilter("");
    setValueFrom("");
    setValueTo("");
    setStartFrom("");
    setStartTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [
      ["Code", "Type", "Value", "Cap", "Applies to", "Condition", "Use limit", "Used", "From", "To", "Status"],
    ];
    (exportData.getTripPromotionsByAdmin || []).forEach((p) => {
      rows.push([
        p.promotionCode || "-",
        TYPE_LABELS[p.type] || p.type || "-",
        valueOf(p),
        p.maximumPromotion ? `$${Number(p.maximumPromotion).toFixed(2)}` : "-",
        FOR_LABELS[p.for] || p.for || "-",
        CONDITION_LABELS[p.condition] || p.condition || "-",
        p.useLimitCount || "-",
        (p.canNotUse && p.canNotUse.length) || 0,
        p.from ? moment(p.from).format("MM/DD/YYYY") : "-",
        p.to ? moment(p.to).format("MM/DD/YYYY") : "-",
        lifecycleOf(p).label,
      ]);
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Trip promotions");
    XLSX.writeFile(wb, "TripPromotions.xlsx");
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (serverCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: serverCount } });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superRidePage superPromotionsPage">
        <main className="main">
          <PageHeader title="Trip promotions" description="Discount codes for rides and deliveries." />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={7} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  const selectedPromotions = promotions.filter((p) => selectedIds.includes(p._id));

  return (
    <div className="page superRidePage superPromotionsPage">
      <main className="main">
        <PageHeader
          title="Trip promotions"
          count={serverCount}
          description="Discount codes for rides and deliveries, and when each one is valid."
          actions={
            <React.Fragment>
              <PageActionButton
                icon={<CloudDownloadIcon fontSize="small" />}
                label={exporting ? "Exporting" : "Export"}
                info={exporting ? "Preparing file" : `${serverCount} code${serverCount === 1 ? "" : "s"}`}
                onClick={handleExport}
                disabled={serverCount === 0}
                loading={exporting}
              />
              <Button
                variant="primary"
                onClick={() => (window.location.href = `${endPoint}/superAdmin/addPromotion`)}
              >
                New promotion
              </Button>
            </React.Fragment>
          }
        />

        <section className="stats">
          <MetricCard icon={<LocalOfferIcon />} title="Matching codes" value={serverCount} />
          <MetricCard
            icon={<PlayCircleOutlineIcon />}
            title="Live"
            value={livePage}
            hint="On this page"
            tone={livePage > 0 ? "success" : "default"}
          />
          <MetricCard icon={<ScheduleIcon />} title="Scheduled" value={scheduledPage} hint="On this page" />
          <MetricCard icon={<EventBusyIcon />} title="Expired" value={expiredPage} hint="On this page" />
        </section>

        <section className="filters">
          <SearchField
            placeholder="Search by promotion code..."
            value={codeFilter}
            onChange={(e) => setCodeFilter(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={typeFilter ? `Type: ${TYPE_LABELS[typeFilter]}` : "Type"}
              options={[{ value: "", label: "All types" }, ...TYPE_OPTIONS]}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
            <FilterDropdown
              label={conditionFilter ? CONDITION_LABELS[conditionFilter] : "Condition"}
              options={[{ value: "", label: "Any condition" }, ...CONDITION_OPTIONS]}
              value={conditionFilter}
              onChange={(e) => setConditionFilter(e.target.value)}
            />
            <FilterDropdown
              label={lifecycleFilter ? LIFECYCLE_LABELS[lifecycleFilter] : "Status"}
              options={LIFECYCLE_OPTIONS}
              value={lifecycleFilter}
              onChange={(e) => setLifecycleFilter(e.target.value)}
            />
            <NumberRangeFilter
              label="Value"
              fromValue={valueFrom}
              toValue={valueTo}
              onFromChange={(e) => setValueFrom(e.target.value)}
              onToChange={(e) => setValueTo(e.target.value)}
            />
            <DateRangeFilter
              label="Starts"
              fromValue={startFrom}
              toValue={startTo}
              onFromChange={(e) => setStartFrom(e.target.value)}
              onToChange={(e) => setStartTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <TableToolbar
          selectedCount={selectedIds.length}
          checkboxState={checkboxState}
          onCheckboxChange={toggleSelectAll}
          bulkActions={
            <Button
              variant="dangerQuiet"
              size="sm"
              icon={<DeleteIcon fontSize="small" />}
              onClick={() => setDeleteTarget(selectedPromotions)}
            >
              Delete selected
            </Button>
          }
          leftContent={
            lifecycleFilter ? (
              <span className="superRidePill">
                {promotions.length} of {allPromotions.length} on this page
              </span>
            ) : null
          }
          rightActions={
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => refetch()}
              title="Refresh promotions"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        />

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superRideSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell tableHeaderCheckbox">
                <Checkbox
                  size="small"
                  checked={checkboxState === "checked"}
                  indeterminate={checkboxState === "indeterminate"}
                  onChange={toggleSelectAll}
                  inputProps={{ "aria-label": "Select every promotion on this page" }}
                />
              </div>
              <div className="tableHeaderCell">Code</div>
              <div className="tableHeaderCell">Discount</div>
              <div className="tableHeaderCell">Applies to</div>
              <div className="tableHeaderCell">Valid</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {promotions.length === 0 ? (
              <EmptyState
                icon={<LocalOfferIcon />}
                title={activeFilterChips.length === 0 ? "No promotions yet" : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? "Create a promotion code to discount rides or deliveries."
                    : "No promotions match these filters. Try widening or clearing them."
                }
              />
            ) : (
              promotions.map((promotion) => {
                const lifecycle = lifecycleOf(promotion);
                const used = (promotion.canNotUse && promotion.canNotUse.length) || 0;
                return (
                  <div className="row" key={promotion._id}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(promotion._id)}
                      onChange={() => toggleSelectOne(promotion._id)}
                      inputProps={{ "aria-label": `Select ${promotion.promotionCode}` }}
                    />

                    <div className="superRideStack" data-label="Code">
                      <span className="superRideValueRow">
                        <span className="superRideMono">{promotion.promotionCode}</span>
                        <IconButton
                          size="small"
                          className="rowActionButton"
                          onClick={() => handleCopy(promotion.promotionCode)}
                          title="Copy code"
                        >
                          {copiedCode === promotion.promotionCode ? (
                            <CheckCircleOutlineIcon fontSize="small" />
                          ) : (
                            <FileCopyIcon style={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </span>
                      <span className="superRideTag">
                        {CONDITION_LABELS[promotion.condition] || promotion.condition}
                      </span>
                    </div>

                    <div className="superRidePerson" data-label="Discount">
                      <span className="superRideAmount">{valueOf(promotion)}</span>
                      {promotion.maximumPromotion ? (
                        <span className="superRidePersonMeta">
                          up to ${Number(promotion.maximumPromotion).toFixed(2)}
                        </span>
                      ) : null}
                    </div>

                    <span className="superRideTag" data-label="Applies to">
                      {FOR_LABELS[promotion.for] || promotion.for || "Everything"}
                    </span>

                    <div className="superRidePerson" data-label="Valid">
                      <span className="superRidePersonName">
                        {formatDate(promotion.from)} — {formatDate(promotion.to)}
                      </span>
                      <span className="superRidePersonMeta">
                        {promotion.useLimitCount
                          ? `${used} of ${promotion.useLimitCount} used`
                          : `${used} used`}
                      </span>
                    </div>

                    <span data-label="Status">
                      <StatusBadge status={lifecycle.tone} label={lifecycle.label} />
                    </span>

                    <div className="actions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() =>
                          (window.location.href = `${endPoint}/superAdmin/editPromotion/${promotion._id}`)
                        }
                        title="Edit promotion"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setPromotionDetail(promotion)}
                        title="Promotion details"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {serverCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={serverCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(promotionDetail)}
        onClose={() => setPromotionDetail(null)}
        title="Promotion details"
        subtitle={promotionDetail ? promotionDetail.promotionCode : ""}
      >
        {promotionDetail && <PromotionDetails promotion={promotionDetail} />}
      </DialogShell>

      <DialogShell
        open={Boolean(deleteTarget)}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        title={deleteTarget && deleteTarget.length > 1 ? "Delete promotions" : "Delete promotion"}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setDeleteTarget(null), disabled: deleting },
          { label: "Delete", onClick: runDelete, variant: "danger", loading: deleting },
        ]}
      >
        <div className="superRideConfirm">
          {deleteTarget && deleteTarget.length > 1
            ? `${deleteTarget.length} promotion codes will be deleted. Anyone holding them will no longer be able to redeem them.`
            : "This promotion code will be deleted. Anyone holding it will no longer be able to redeem it."}
        </div>
      </DialogShell>
    </div>
  );
};

export default Promotions;
